"use client";
import React, { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";

interface Props {
  className?: string;
  children: React.ReactNode;
  previewFromIndex?: number;
  onPreviewComplete?: () => void;
}

const Timeline = ({ className = "", children, previewFromIndex, onPreviewComplete }: Props) => {
  const childArray = React.Children.toArray(children);
  const total = childArray.length;
  const containerRef = useRef<HTMLDivElement>(null);

  // Tracks the highest index that has signalled completion — starts at -1 (nothing done)
  const [completedUpTo, setCompletedUpTo] = useState(-1);

  const handleComplete = (index: number) => {
    setCompletedUpTo((prev) => Math.max(prev, index));
  };

  // If the page is reloaded while scrolled past the timeline start, snap back to
  // the timeline top so item 0 enters view and the animation chain can begin.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > top) {
        window.scrollTo({ top, behavior: "instant" });
      }
    });
  }, []);

  const enhancedChildren = childArray.map((child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(
        child as React.ReactElement<{
          isFirst?: boolean;
          isLast?: boolean;
          index?: number;
          canAnimate?: boolean;
          onComplete?: (index: number) => void;
          isPreview?: boolean;
          onPreviewComplete?: () => void;
        }>,
        {
          isFirst: index === 0,
          isLast: index === total - 1,
          index,
          canAnimate: index === 0 || completedUpTo >= index - 1,
          onComplete: handleComplete,
          isPreview: previewFromIndex !== undefined && index >= previewFromIndex,
          onPreviewComplete: previewFromIndex !== undefined && index === previewFromIndex ? onPreviewComplete : undefined,
        }
      );
    }
    return child;
  });

  return (
    <div ref={containerRef} className={`mx-[4%] mt-16 mb-16 ${className}`}>
      <h1 className="text-5xl">Timeline</h1>
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1, transition: { duration: 0.7, delay: 0.4 } }}
        viewport={{ once: true }}
        className="bg-(--uforeground2) h-0.75 mb-10"
        style={{ transformOrigin: "center" }}
      />
      <div className="flex flex-col">{enhancedChildren}</div>
    </div>
  );
};

export default Timeline;
